import { trapTab, focusFirstWithin, restoreFocus } from '@/utils/ggFocusTrap';

/**
 * Dialog semantics for the Globo Graphics drawers.
 *
 * These panels were `v-dialog` before the redesign. Vuetify's VDialog sets
 * `role="dialog"` and `aria-modal`, closes on Esc, and traps focus; VNavigationDrawer
 * does none of that — it has no role, no aria, and no keydown handling at all. So the
 * conversion silently dropped dialog semantics: nothing announces the drawer to a
 * screen reader, Esc does nothing, and Tab walks out into the page behind.
 *
 * The ARIA half is on the elements themselves (`role="dialog"`, `aria-modal`,
 * `aria-labelledby`), because those belong in the template. This is the keyboard half:
 * Esc closes, Tab is contained, focus moves into the drawer on open and back to the
 * trigger on close — a drawer that took focus and then dropped it on <body> would
 * leave a keyboard user at the top of the document with no way back.
 *
 * The trap itself lives in `@/utils/ggFocusTrap` because the fifth GG drawer (the
 * Graphic Hub browse panel) is not its host's root element and owns its open state via
 * v-model, so it cannot use this mixin — but it must not carry a second copy of the
 * logic either.
 *
 * Requires the host to expose a `value` prop, to close via `input`, and for the drawer
 * to BE the component's root element — true of all four (`:value="value"` +
 * `@input="$emit('input', $event)"`).
 */
export default {
  data() {
    return {
      // The element that had focus when the drawer opened, so it can be handed back.
      ggDrawerReturnFocus: null,
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(open, wasOpen) {
        if (open) {
          document.addEventListener('keydown', this.onGgDrawerKeydown);
          // $nextTick so the body has rendered — focusing before that finds nothing
          // and drops focus on the drawer container instead of its first control.
          this.$nextTick(() => {
            this.ggDrawerReturnFocus = focusFirstWithin(this.$el);
          });
        } else {
          document.removeEventListener('keydown', this.onGgDrawerKeydown);
          // `wasOpen === undefined` is the immediate:true run at mount, not a close —
          // restoring there would steal focus from wherever the page legitimately put it.
          if (wasOpen) {
            restoreFocus(this.ggDrawerReturnFocus);
            this.ggDrawerReturnFocus = null;
          }
        }
      },
    },
  },
  beforeDestroy() {
    // The listener is on document, so it outlives the component unless removed —
    // and a drawer destroyed while open would otherwise leave it bound forever.
    document.removeEventListener('keydown', this.onGgDrawerKeydown);
  },
  methods: {
    onGgDrawerKeydown(event) {
      if (event.key === 'Escape') {
        this.$emit('input', false);
        return;
      }
      trapTab(this.$el, event);
    },
  },
};
